/**
 * Modelo del menú lateral: lógica pura, sin Vue ni Vuetify.
 *
 * `Permisos.js` dice QUÉ módulos ve cada rol; este archivo lo convierte en lo
 * que pintan el drawer y el buscador de módulos. Mantenerlo aparte hace que
 * migrar a Vuetify 3 solo toque los componentes, no esta lógica.
 */

// Los grupos del menú no traen icono propio (el campo `icon` de Permisos.js es
// un resto de Material); el rail de tablet necesita uno por grupo.
export const GROUP_ICONS = {
    Catalogos: 'fas fa-book',
    Productos: 'fas fa-th-list',
    Entradas: 'fas fa-cart-arrow-down',
    Inventario: 'fas fa-warehouse',
    Ventas: 'fas fa-cash-register',
    'Ingresos/Egresos': 'fas fa-exchange-alt',
    Transacciones: 'fas fa-exchange-alt',
    Personal: 'fas fa-user-tie',
    Perfil: 'fas fa-user-shield',
    Reportes: 'fas fa-chart-bar',
    Importaciones: 'fas fa-file-import'
};
const DEFAULT_GROUP_ICON = 'fas fa-folder';

function toLink(item, index, group) {
    return {
        type: 'link',
        key: `link-${group || 'root'}-${item.text}-${index}`,
        text: item.text,
        icon: item.icon || DEFAULT_GROUP_ICON,
        to: item.to,
        exact: item.exact === true,
        isNew: item.new === true,
        isUpdated: item.update === true,
        group: group || ''
    };
}

/**
 * Items de `createPermissions(rol)` -> árbol listo para pintar.
 *
 * - `{ heading }` -> encabezado suelto.
 * - Un grupo con UN solo hijo se aplana a un enlace: "Inventario > Inventario"
 *   no necesita un nivel más. Conserva `group` para el buscador.
 * - Un grupo con varios hijos queda como grupo con icono propio.
 */
export function buildNav(items) {
    return (items || []).filter(Boolean).map((item, index) => {
        if (item.heading) {
            return { type: 'heading', key: `heading-${index}-${item.heading}`, text: item.heading };
        }
        if (Array.isArray(item.children) && item.children.length) {
            if (item.children.length === 1) {
                return toLink(item.children[0], 0, item.text);
            }
            return {
                type: 'group',
                key: `group-${item.text}`,
                text: item.text,
                icon: GROUP_ICONS[item.text] || DEFAULT_GROUP_ICON,
                children: item.children.map((child, i) => toLink(child, i, item.text))
            };
        }
        return toLink(item, index, '');
    });
}

/** Todos los enlaces del árbol en una lista plana (para el buscador). */
export function flattenNav(nav) {
    const links = [];
    (nav || []).forEach((item) => {
        if (item.type === 'link') links.push(item);
        if (item.type === 'group') links.push(...item.children);
    });
    return links;
}

const fold = (text) => String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** Filtra enlaces por nombre o grupo, sin acentos ni mayúsculas. */
export function searchNav(links, query) {
    const needle = fold(query).trim();
    if (!needle) return links;
    return links.filter((link) => fold(link.text).includes(needle) || fold(link.group).includes(needle));
}

/** `/controlPanel/ventas/12` cuenta como dentro de `/controlPanel/ventas`. */
export function isPathActive(currentPath, targetPath) {
    if (!targetPath || !currentPath) return false;
    const base = targetPath.endsWith('/') ? targetPath : `${targetPath}/`;
    return currentPath === targetPath || currentPath.startsWith(base);
}

/**
 * Clave del grupo que contiene la ruta actual, o null.
 * `resolvePath` convierte el `to` de un enlace (string u objeto de ruta) en
 * path; lo da el componente porque depende del router.
 */
export function activeGroupKey(nav, currentPath, resolvePath) {
    const group = (nav || []).find((item) => item.type === 'group'
        && item.children.some((child) => isPathActive(currentPath, resolvePath(child.to))));
    return group ? group.key : null;
}

/**
 * Cómo se muestra el menú según el ancho:
 * - `full`: sidebar completo (escritorio, 1264px o más).
 * - `rail`: rail de iconos fijo + drawer temporal al tocar la hamburguesa (tablet).
 * - `overlay`: solo drawer temporal (teléfono).
 * Recibe el nombre del breakpoint (`xs|sm|md|lg|xl`), igual en Vuetify 2 y 3.
 */
export function navModeFor(breakpointName) {
    if (breakpointName === 'xs') return 'overlay';
    if (breakpointName === 'sm' || breakpointName === 'md') return 'rail';
    return 'full';
}

/** `ROLE_ADMINISTRATOR_SUCURSAL` -> `Administrator sucursal`. */
export function roleLabel(roleName) {
    const text = String(roleName || '').replace(/^ROLE_/, '').replace(/_/g, ' ').toLowerCase();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

const MINI_KEY = 'gsf.nav.mini';

/** Preferencia del escritorio (sidebar completo o rail). El storage puede fallar. */
export function loadNavMini() {
    try {
        return window.localStorage.getItem(MINI_KEY) === '1';
    } catch (e) {
        return false;
    }
}

export function saveNavMini(mini) {
    try {
        window.localStorage.setItem(MINI_KEY, mini ? '1' : '0');
    } catch (e) {
        // sin storage: la preferencia dura lo que la sesión
    }
}
